// What the order gate found for this buyer, shown above the Spiffy form.
// Hook + contract: lib/useOrderGate.ts. Wording of each line is Command
// Centre's buyer_label (authored with the rules), never hard-coded here.
//
//   auto  benefit    → a confirmation line ("Member Pro pricing applied…")
//   ask   credit     → "Use my free course" — grants the program, no checkout
//         credit with unlocks_at → owed but not usable yet (the FRWY launch
//                      course waits out the 30-day trial): the date, no button
//   ask   promotion  → "Apply it" — reloads the page with the code applied

import { useState } from 'react';
import type { GateOffer, ClaimOutcome } from '../lib/useOrderGate';

const ORANGE = '#F05A28';

export function OrderGatePanel({
  offers,
  onClaim,
  onAccept,
  onClaimed,
  dark = false,
}: {
  offers: GateOffer[];
  onClaim: (ruleKey: string) => Promise<ClaimOutcome>;
  onAccept: (ruleKey: string) => void;
  onClaimed: (outcome: Extract<ClaimOutcome, { ok: true }>) => void;
  dark?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [declined, setDeclined] = useState<Set<string>>(new Set());
  if (offers.length === 0) return null;

  const applied = offers.filter((o) => o.applied);
  const credits = offers.filter((o) => o.kind === 'credit' && !declined.has(o.rule_key));
  const promos = offers.filter((o) => !o.applied && o.kind !== 'credit' && o.mode === 'ask');

  const box: React.CSSProperties = {
    border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : '#E2E8F0'}`,
    background: dark ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
    borderRadius: 12,
    padding: '14px 16px',
    margin: '0 0 12px',
    color: dark ? '#F8FAFC' : '#0F172A',
    fontSize: 15,
    lineHeight: 1.5,
  };
  const appliedBox: React.CSSProperties = { ...box, borderColor: dark ? 'rgba(74,222,128,0.5)' : '#86EFAC', background: dark ? 'rgba(74,222,128,0.10)' : '#F0FDF4' };
  const primary: React.CSSProperties = {
    padding: '10px 18px', borderRadius: 8, border: 'none', background: ORANGE, color: '#fff',
    fontSize: 15, fontWeight: 600, cursor: 'pointer', marginRight: 10, marginTop: 10,
  };
  const secondary: React.CSSProperties = {
    padding: '10px 14px', borderRadius: 8, border: `1px solid ${dark ? 'rgba(255,255,255,0.3)' : '#CBD5E1'}`,
    background: 'transparent', color: dark ? '#F8FAFC' : '#334155', fontSize: 15, cursor: 'pointer', marginTop: 10,
  };

  const claim = async (ruleKey: string) => {
    setBusy(ruleKey);
    setError(null);
    const r = await onClaim(ruleKey);
    setBusy(null);
    if (r.ok) onClaimed(r);
    else setError(r.error);
  };

  return (
    <div data-testid="order-gate-panel" style={{ margin: '0 0 20px' }}>
      {applied.map((o) => (
        <div key={o.rule_key} style={appliedBox} data-testid={`gate-applied-${o.rule_key}`}>
          <strong>{o.label}</strong>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            It’s already on the order form below. Nothing to enter.
          </div>
        </div>
      ))}
      {credits.map((o) => o.unlocks_at ? (
        <div key={o.rule_key} style={box} data-testid={`gate-credit-locked-${o.rule_key}`}>
          <div>{o.label}</div>
          <div style={{ fontSize: 14, opacity: 0.85, marginTop: 6 }}>
            Your free course unlocks on <strong>{unlockDay(o.unlocks_at)}</strong>, once your 30-day trial is
            over. Come back then and choose any program.
          </div>
        </div>
      ) : (
        <div key={o.rule_key} style={box} data-testid={`gate-credit-${o.rule_key}`}>
          <div>{o.label}</div>
          <button type="button" style={primary} disabled={busy !== null} onClick={() => claim(o.rule_key)}>
            {busy === o.rule_key ? 'Applying your free course…' : 'Use my free course'}
          </button>
          <button
            type="button"
            style={secondary}
            disabled={busy !== null}
            onClick={() => setDeclined((d) => new Set(d).add(o.rule_key))}
          >
            Save it for later
          </button>
        </div>
      ))}
      {promos.map((o) => (
        <div key={o.rule_key} style={box} data-testid={`gate-promo-${o.rule_key}`}>
          <div>{o.label}</div>
          <button type="button" style={primary} onClick={() => onAccept(o.rule_key)}>
            Apply it
          </button>
        </div>
      ))}
      {error && (
        <p role="alert" style={{ color: '#B91C1C', fontSize: 14, margin: '4px 0 0' }}>{error}</p>
      )}
    </div>
  );
}

function unlockDay(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long', month: 'long', day: 'numeric' });
}

/** Shown instead of the order form once a free course has been used. */
export function CreditClaimedPanel({
  outcome,
  email,
  dark = false,
}: {
  outcome: Extract<ClaimOutcome, { ok: true }>;
  email: string;
  dark?: boolean;
}) {
  const program = outcome.program_label || 'your program';
  return (
    <div
      data-testid="order-gate-claimed"
      style={{
        border: `1px solid ${dark ? 'rgba(74,222,128,0.5)' : '#86EFAC'}`,
        background: dark ? 'rgba(74,222,128,0.10)' : '#F0FDF4',
        borderRadius: 14,
        padding: '22px 22px',
        color: dark ? '#F8FAFC' : '#0F172A',
        lineHeight: 1.55,
      }}
    >
      <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>You’re in. {program} is yours.</h2>
      {outcome.status === 'granted' ? (
        <p style={{ margin: 0 }}>
          We’ve used your free course, so there’s nothing to pay. Your welcome email is on its
          way to <strong>{email}</strong> with everything you need to get started.
        </p>
      ) : (
        <p style={{ margin: 0 }}>
          We’ve used your free course, so there’s nothing to pay. Our team is setting up your
          access now and will email <strong>{email}</strong> within one business day.
        </p>
      )}
    </div>
  );
}
